// Community UI mods, WoW-addon style, but sandboxed.
//
// Each enabled mod runs in its own <iframe sandbox="allow-scripts"> with a strict content policy:
//   - it can't touch the game page, your sign-in cookie or the game's storage (it has no origin at all)
//   - it can't reach the network (no fetch, no images from elsewhere), so what it sees stays on your device
//   - it can't act for you: no moving, chatting, trading or buying. It can read game events, draw its own
//     window, print lines only you see in the chat box, keep small saved data, recolor the interface and
//     hide a few HUD pieces.
// The game talks to mods with postMessage and checks every message comes from that mod's own frame.
// Start the game with ?safe=1 to load with every mod off.

export interface ModInfo { id: string; name: string; version: string; author: string; description: string; code: string; enabled: boolean; builtin?: boolean }
export type ModEvent = 'ready' | 'position' | 'chat' | 'party' | 'inventory' | 'quests' | 'skills' | 'notice' | 'players';

const KEY = 'tt.mods.v1', STORE = 'tt.modstore.';
const MAX_STORE = 64 * 1024, MAX_CODE = 200 * 1024;
const THEME_VARS = ['--bg', '--card', '--ink', '--muted', '--line', '--accent', '--accent-deep'];
const HUD_PARTS: Record<string, string> = { keys: '.keys', tracker: '#tracker', chatHint: '.chat-hint', party: '#party', stats: '#debug', toasts: '#toasts' };
const COLOR = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\))$/i;

/** Reads the header comment: // @name ..., // @version ..., // @author ..., // @description ... */
export function parseMod(code: string): Omit<ModInfo, 'id' | 'enabled'> | null {
  if (typeof code !== 'string' || !code.trim() || code.length > MAX_CODE) return null;
  const tag = (k: string) => (new RegExp(`^\\s*//\\s*@${k}\\s+(.+)$`, 'm').exec(code)?.[1] ?? '').trim().slice(0, 120);
  const name = tag('name'); if (!name) return null;
  return { name, version: tag('version') || '1.0', author: tag('author') || 'unknown', description: tag('description'), code };
}

interface Running { mod: ModInfo; frame: HTMLIFrameElement; win: HTMLElement; subs: Set<ModEvent>; lastPrint: number; error: string }

export class ModHost {
  mods: ModInfo[] = [];
  readonly safe = new URLSearchParams(location.search).get('safe') === '1';
  private running = new Map<string, Running>();
  private hidden = new Map<string, Set<string>>();     // mod id -> HUD parts it hides
  private themed = new Map<string, Record<string, string>>();
  private last: Partial<Record<ModEvent, unknown>> = {};
  /** Where mods print: a chat line only you see. */
  print: (mod: string, text: string) => void = () => {};
  onChange: () => void = () => {};

  constructor(private builtins: Omit<ModInfo, 'enabled'>[]) {
    let saved: ModInfo[] = [];
    try { saved = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { /* fresh */ }
    const byId = new Map(saved.map(m => [m.id, m]));
    this.mods = [...builtins.map(b => ({ ...b, enabled: byId.get(b.id)?.enabled ?? false, builtin: true })), ...saved.filter(m => !m.builtin)];
    addEventListener('message', e => this.onMessage(e));
  }

  start(): void { if (!this.safe) for (const m of this.mods) if (m.enabled) this.run(m); }
  stopAll(): void { for (const id of [...this.running.keys()]) this.stop(id); }
  errorOf(id: string): string { return this.running.get(id)?.error ?? ''; }
  isRunning(id: string): boolean { return this.running.has(id); }

  install(code: string): { ok: true; mod: ModInfo } | { ok: false; msg: string } {
    const p = parseMod(code); if (!p) return { ok: false, msg: 'That doesn\'t look like a mod: it needs a "// @name" line at the top (and must be under 200 KB).' };
    const id = 'user-' + p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
    const existing = this.mods.findIndex(m => m.id === id);
    const mod: ModInfo = { ...p, id, enabled: true };
    if (existing >= 0) { if (this.mods[existing].builtin) return { ok: false, msg: 'A built-in mod already has that name.' }; this.stop(id); this.mods[existing] = mod; } else this.mods.push(mod);
    this.save(); if (!this.safe) this.run(mod); this.onChange();
    return { ok: true, mod };
  }
  remove(id: string): void { this.stop(id); this.mods = this.mods.filter(m => m.id !== id || m.builtin); try { localStorage.removeItem(STORE + id); } catch { /* */ } this.save(); this.onChange(); }
  setEnabled(id: string, on: boolean): void {
    const m = this.mods.find(x => x.id === id); if (!m) return;
    m.enabled = on; this.save();
    if (on && !this.safe) this.run(m); else this.stop(id);
    this.onChange();
  }
  private save(): void { try { localStorage.setItem(KEY, JSON.stringify(this.mods.map(m => m.builtin ? { id: m.id, enabled: m.enabled, builtin: true } : m))); } catch { /* full */ } }

  /** The game reports something happened. Each event goes only to mods that asked for it. */
  emit(ev: ModEvent, data: unknown): void {
    this.last[ev] = data;
    for (const r of this.running.values()) if (r.subs.has(ev)) this.post(r, { t: 'event', ev, data });
  }

  // ---------- running a mod ----------
  private run(mod: ModInfo): void {
    if (this.running.has(mod.id)) return;
    const win = document.createElement('section'); win.className = 'mod-win'; win.hidden = true; win.setAttribute('aria-label', mod.name);
    const head = document.createElement('header'); head.className = 'mod-head';
    const title = document.createElement('span'); title.textContent = mod.name;
    const min = document.createElement('button'); min.type = 'button'; min.textContent = '–'; min.setAttribute('aria-label', 'Minimize');
    const close = document.createElement('button'); close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', 'Hide');
    head.append(title, min, close);
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-scripts');               // no same-origin: no cookies, storage or page access
    frame.setAttribute('referrerpolicy', 'no-referrer');
    frame.title = mod.name;
    frame.srcdoc = srcdoc(mod.code);
    win.append(head, frame);
    document.body.append(win);
    const r: Running = { mod, frame, win, subs: new Set(), lastPrint: 0, error: '' };
    this.running.set(mod.id, r);
    min.addEventListener('click', () => { frame.hidden = !frame.hidden; win.classList.toggle('min', frame.hidden); });
    close.addEventListener('click', () => { win.hidden = true; });
    this.draggable(win, head, mod.id);
  }

  private stop(id: string): void {
    const r = this.running.get(id); if (!r) return;
    r.win.remove(); this.running.delete(id);
    this.hidden.delete(id); this.themed.delete(id); this.applyHud(); this.applyTheme();
  }

  /** Brings back a mod window the player closed. */
  show(id: string): void { const r = this.running.get(id); if (r) { r.win.hidden = false; r.frame.hidden = false; r.win.classList.remove('min'); } }

  private post(r: Running, msg: unknown): void { r.frame.contentWindow?.postMessage(msg, '*'); }

  private onMessage(e: MessageEvent): void {
    const r = [...this.running.values()].find(x => x.frame.contentWindow === e.source); if (!r) return;   // only from a mod's own frame
    const m = e.data as Record<string, unknown>; if (!m || typeof m !== 'object') return;
    try {
      switch (m.t) {
        case 'on': {
          const ev = String(m.ev) as ModEvent;
          if (!['ready', 'position', 'chat', 'party', 'inventory', 'quests', 'skills', 'notice', 'players'].includes(ev)) return;
          r.subs.add(ev); if (this.last[ev] !== undefined) this.post(r, { t: 'event', ev, data: this.last[ev] });
          return;
        }
        case 'panel': {
          const w = clamp(Number(m.width) || 220, 120, 520), h = clamp(Number(m.height) || 140, 40, 600);
          r.win.style.width = w + 'px'; r.frame.style.height = h + 'px';
          if (typeof m.title === 'string') r.win.querySelector('.mod-head span')!.textContent = m.title.slice(0, 40);
          if (r.win.hidden && !this.restorePos(r.win, r.mod.id)) {   // first time: stack under the others on the right
            let y = 130; for (const o of this.running.values()) if (o !== r && !o.win.hidden) y = Math.max(y, o.win.getBoundingClientRect().bottom + 8);
            this.place(r.win, innerWidth - w - 14, Math.min(y, innerHeight - 80));
          }
          r.win.hidden = false;
          return;
        }
        case 'print': {
          const now = performance.now(); if (now - r.lastPrint < 250) return; r.lastPrint = now;
          this.print(r.mod.name, String(m.text ?? '').replace(/[\u0000-\u001f]/g, ' ').slice(0, 200));
          return;
        }
        case 'store': {
          let all: Record<string, unknown> = {};
          try { all = JSON.parse(localStorage.getItem(STORE + r.mod.id) || '{}'); } catch { /* */ }
          if (m.op === 'set') {
            all[String(m.key).slice(0, 64)] = m.value;
            const s = JSON.stringify(all);
            if (s.length > MAX_STORE) return this.post(r, { t: 'store', id: m.id, error: 'Storage full (64 KB per mod).' });
            try { localStorage.setItem(STORE + r.mod.id, s); } catch { /* */ }
            return this.post(r, { t: 'store', id: m.id, value: true });
          }
          return this.post(r, { t: 'store', id: m.id, value: all[String(m.key)] ?? null });
        }
        case 'theme': {
          const vars: Record<string, string> = {};
          for (const [k, v] of Object.entries((m.vars ?? {}) as Record<string, unknown>)) if (THEME_VARS.includes(k) && typeof v === 'string' && COLOR.test(v.trim())) vars[k] = v.trim();
          this.themed.set(r.mod.id, vars); this.applyTheme(); return;
        }
        case 'hide': {
          const parts = new Set((Array.isArray(m.parts) ? m.parts : []).map(String).filter(p => p in HUD_PARTS));
          this.hidden.set(r.mod.id, parts); this.applyHud(); return;
        }
        case 'error': r.error = String(m.message ?? 'error').slice(0, 200); this.onChange(); return;
      }
    } catch (err) { r.error = String(err); }
  }

  private applyTheme(): void {
    const root = document.documentElement.style;
    for (const k of THEME_VARS) root.removeProperty(k);
    for (const vars of this.themed.values()) for (const [k, v] of Object.entries(vars)) root.setProperty(k, v);
  }
  private applyHud(): void {
    const all = new Set<string>(); for (const s of this.hidden.values()) for (const p of s) all.add(p);
    for (const [p, sel] of Object.entries(HUD_PARTS)) document.querySelectorAll<HTMLElement>(sel).forEach(e => e.classList.toggle('mod-hidden', all.has(p)));
  }

  // ---------- windows ----------
  private draggable(win: HTMLElement, handle: HTMLElement, id: string): void {
    let sx = 0, sy = 0, ox = 0, oy = 0, on = false;
    handle.addEventListener('pointerdown', e => { if ((e.target as HTMLElement).tagName === 'BUTTON') return; on = true; sx = e.clientX; sy = e.clientY; const r = win.getBoundingClientRect(); ox = r.left; oy = r.top; handle.setPointerCapture(e.pointerId); });
    handle.addEventListener('pointermove', e => { if (!on) return; this.place(win, ox + e.clientX - sx, oy + e.clientY - sy); });
    handle.addEventListener('pointerup', () => { if (!on) return; on = false; try { localStorage.setItem('tt.modpos.' + id, JSON.stringify([win.offsetLeft, win.offsetTop])); } catch { /* */ } });
  }
  private place(win: HTMLElement, x: number, y: number): void {
    win.style.left = clamp(x, 0, innerWidth - 80) + 'px'; win.style.top = clamp(y, 0, innerHeight - 30) + 'px'; win.style.right = 'auto';
  }
  private restorePos(win: HTMLElement, id: string): boolean {
    try { const p = JSON.parse(localStorage.getItem('tt.modpos.' + id) || 'null'); if (Array.isArray(p)) { this.place(win, p[0], p[1]); return true; } } catch { /* */ }
    return false;
  }
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** The page a mod runs in: a strict policy, the `tt` API, then the mod's own code. */
function srcdoc(code: string): string {
  const api = `
'use strict';
(() => {
  const handlers = {}, waiting = {}; let n = 0;
  const send = m => parent.postMessage(m, '*');
  addEventListener('message', e => {
    if (e.source !== parent) return;
    const m = e.data || {};
    if (m.t === 'event') (handlers[m.ev] || []).forEach(f => { try { f(m.data); } catch (err) { send({ t: 'error', message: String(err && err.message || err) }); } });
    if (m.t === 'store' && waiting[m.id]) { waiting[m.id](m.error ? Promise.reject(new Error(m.error)) : m.value); delete waiting[m.id]; }
  });
  addEventListener('error', e => send({ t: 'error', message: e.message }));
  const ask = (op, key, value) => new Promise(res => { const id = ++n; waiting[id] = res; send({ t: 'store', op, key, value, id }); });
  window.tt = Object.freeze({
    on(ev, fn) { (handlers[ev] = handlers[ev] || []).push(fn); send({ t: 'on', ev }); },
    panel(o = {}) { send({ t: 'panel', title: o.title, width: o.width, height: o.height }); },
    print(text) { send({ t: 'print', text: String(text) }); },
    theme(vars) { send({ t: 'theme', vars }); },
    hide(parts) { send({ t: 'hide', parts }); },
    storage: Object.freeze({ get: k => ask('get', k), set: (k, v) => ask('set', k, v) }),
  });
})();`;
  const safe = (s: string) => s.replace(/<\/script/gi, '<\\/script');
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:">
<style>html,body{margin:0;font:600 12px/1.35 system-ui,sans-serif;color:#2f2a26;background:#fffaf0}body{padding:6px 8px}</style>
<script>${safe(api)}</script></head><body><script>
try {
${safe(code)}
} catch (e) { parent.postMessage({ t: 'error', message: String(e && e.message || e) }, '*'); }
</script></body></html>`;
}
